<template>
	<div class="bg">
		<div class="page">
			<div class="title">
				<div class="title_h3">
					Detailed <span style="color: #2A6EBE;">Information</span>
				</div>
			</div>

			<div class="box1" :class="{ 'spectroscopy-active': isSpectroscopyActive }">
				<div
					class="structure"
					:style="{
						width: structureDisplay === 'max' ? '100%' : (structureDisplay === 'min' ? '0%' : (isSpectroscopyActive ? '20%' : '25%')),
						display: structureDisplay === 'min' ? 'none' : 'block'
					}"
				>
					<div class="layout-control">
						<button @click="toggleLayout('structure', 'max')" v-if="structureDisplay !== 'max'">
							🖥️ Maximize
						</button>
						<button @click="toggleLayout('structure', 'min')" v-if="structureDisplay !== 'min'">
							➖ Minimize
						</button>
						<button @click="toggleLayout('structure', 'normal')" v-if="structureDisplay !== 'normal'">
							🔙 Restore
						</button>
					</div>

					<div class="structure_title">{{ data.data_no }}</div>

					<div class="box">
						<div class="box_list" @click="switchover(0)">
							<span>{{ data.model }}</span>
						</div>
						<div class="box_list">
							<span v-if="data.coordination_number">
								Coordination Number: {{ parseInt(data.coordination_number) }}
							</span>
						</div>
						<div class="box_list" @click="switchover(1)" v-if="data.cdr_path && data.cdr_path.length > 1">
							<span>model_O₂</span>
						</div>
						<div class="box_list" @click="toggleChartType">
							<span>
								Spectroscopy
								<div>(Show Theoretical Match)</div>
							</span>
						</div>
					</div>
				</div>

				<div
					class="chart_container"
					:style="{
						flex: chartDisplay === 'max' ? '1 1 100%' : (chartDisplay === 'min' ? '0' : '1'),
						display: chartDisplay === 'min' ? 'none' : 'block'
					}"
				>
					<div class="layout-control">
						<button @click="toggleLayout('chart', 'max')" v-if="chartDisplay !== 'max'">
							🖥️ Maximize
						</button>
						<button @click="toggleLayout('chart', 'min')" v-if="chartDisplay !== 'min'">
							➖ Minimize
						</button>
						<button @click="toggleLayout('chart', 'normal')" v-if="chartDisplay !== 'normal'">
							🔙 Restore
						</button>
					</div>

					<div class="chart_placeholder">
						<div class="chart-item model-item" v-show="currentChartType === 'model'">
							<iframe
								v-if="iframeSrc"
								:src="iframeSrc"
								width="100%"
								frameborder="0"
								height="100%"
								ref="iframe"
								style="overflow: hidden; border-radius: 33px;"
							></iframe>
						</div>

						<div class="chart-item line-chart-item" v-show="currentChartType === 'line'">
							<div class="model_switch_box" style="margin-bottom: 15px;">
								<button
									v-if="group1Visible"
									class="model_switch_btn"
									:class="{ active: currentGroup === 'group1' }"
									@click="switchGroup('group1')"
								>
									{{ group1Title }}
								</button>
								<button
									v-if="group2Visible"
									class="model_switch_btn"
									:class="{ active: currentGroup === 'group2' }"
									@click="switchGroup('group2')"
								>
									{{ group2Title }}
								</button>
							</div>

							<div class="xanes_chart_container" ref="xanesChart" style="width:100%;height:100%;"></div>

							<div class="xanes_empty" v-if="isCurrentGroupDataEmpty && !isCurrentGroupLoading">
								No {{ currentGroupTitle }} data available
							</div>
							<div class="xanes_empty" v-if="isCurrentGroupLoading">
								Loading {{ currentGroupTitle }} data...
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="box2">
				<div class="property_section">
					<div class="section_title">Property</div>
					<div class="property_grid" v-if="data">
						<div class="property_item">
							<span class="item_label">phi:</span>
							<span class="item_value" v-if="data.phi">{{ data.phi.toFixed(5) }}</span>
						</div>
						<div class="property_item">
							<span class="item_label">Charge of the Central Metal Atom:</span>
							<span class="item_value" v-if="data.bander_charge">{{ data.bander_charge.toFixed(5) }}</span>
						</div>
						<div class="property_item">
							<span class="item_label">Magnetic Moment from OSZICAR:</span>
							<span class="item_value" v-if="data.mag_oszicar">{{ data.mag_oszicar.toFixed(5) }}</span>
						</div>
						<div class="property_item">
							<span class="item_label">Magnetic Moment from OUTCAR:</span>
							<span class="item_value" v-if="data.mag_outcar">{{ data.mag_outcar.toFixed(5) }}</span>
						</div>
						<div class="property_item">
							<span class="item_label">d-Band Center (Spin-Up):</span>
							<span class="item_value" v-if="data.spin_up">{{ data.spin_up.toFixed(5) }}</span>
						</div>
						<div class="property_item">
							<span class="item_label">d-Band Center (Spin-Down):</span>
							<span class="item_value" v-if="data.o2_mag_outcar">{{ data.o2_mag_outcar.toFixed(5) }}</span>
						</div>
						<div class="property_item">
							<span class="item_label">d-Band Center Gap of Spin State:</span>
							<span class="item_value" v-if="data.spin_up">{{ data.spin_up.toFixed(5) }}</span>
						</div>
					</div>
				</div>

				<div class="oxygen_adsorbed_section">
					<div class="section_title">Oxygen-Adsorbed Property</div>
					<div class="adsorbed_grid" v-if="data">
						<el-descriptions direction="vertical" :column="2" border>
							<el-descriptions-item
								label="Charge of the Central Metal Atom"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_bander_charge"
							>
								{{ data.o2_bander_charge.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Magnetic Moment from OSZICAR"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mag_oszicar"
							>
								{{ data.o2_mag_oszicar.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Magnetic Moment from OUTCAR"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mag_outcar"
							>
								{{ data.o2_mag_outcar.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="d-Band Center (Spin-Up)"
								contentStyle="color: #2A6EBE"
								v-if="data.spin_up_2"
							>
								{{ data.spin_up_2.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Bond Length of M–O"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mo"
							>
								{{ data.o2_mo.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Bond Length of O–O"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mag_m"
							>
								{{ data.o2_mag_m.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Magnetic Moment of the Oxygen Atom Bonded to M"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_oo"
							>
								{{ data.o2_oo.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Magnetic Moment of the Oxygen Atom Away from M"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mag_o"
							>
								{{ data.o2_mag_o.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="d-Band Center Gap of Spin State"
								contentStyle="color: #2A6EBE"
								v-if="data.spin_up_2"
							>
								{{ data.spin_up_2.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Total Magnetic Moment of O₂"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mag_oszicar"
							>
								{{ data.o2_mag_oszicar.toFixed(5) }}
							</el-descriptions-item>
							<el-descriptions-item
								label="Adsorption Energy of O₂"
								contentStyle="color: #2A6EBE"
								v-if="data.o2_mag_outcar"
							>
								{{ data.o2_mag_outcar.toFixed(5) }}
							</el-descriptions-item>
						</el-descriptions>
					</div>
				</div>
			</div>

			<div class="bottom_section">
				<div class="bottom_item">
					<div class="section_title">Performance & Synthesis</div>
					<div class="bottom_content">
						<div class="bottom_title" style="border-bottom: none; margin-bottom:0;">Performance</div>
						<div class="property_grid" style="margin-bottom: 20px;">
							<div class="property_item">
								<span class="item_label">Predicted performance:</span>
								<span class="item_value" v-if="data.predicted_performance">{{ data.predicted_performance }}</span>
							</div>
							<div class="property_item">
								<span class="item_label">Metal atom leaching energy:</span>
								<span class="item_value" v-if="data.metal_atom_leaching_energy">{{ data.metal_atom_leaching_energy }}</span>
							</div>
							<div class="property_item">
								<span class="item_label">Calculated energy barrier for hydrogen peroxide decomposition:</span>
								<span class="item_value" v-if="data.barrier">{{ data.barrier }}</span>
							</div>
						</div>
						<div class="bottom_title" style="border-bottom: none; margin-bottom:0;">
							Synthesis Method - Powered by MatDataMiner
						</div>
						<div class="synthesis-method">
							{{ data.method }}
						</div>
					</div>
				</div>
			</div>

			<div>
				<div class="bottom_item">
					<div class="bottom_title">References</div>
					<div class="bottom_content">{{ data.reference }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import * as echarts from 'echarts'

export default {
	name: 'StudentFeedback',
	components: {},
	data() {
		return {
			iframeSrc: '',
			data: {},
			xanesData: {
				group1: { simulation: [], experiment: [] },
				group2: { simulation: [], experiment: [] }
			},
			xanesLoading: {
				group1: { simulation: false, experiment: false },
				group2: { simulation: false, experiment: false }
			},
			currentGroup: 'group1',
			xanesChartInstance: null,
			checkInterval: null,
			handleResize: null,
			isSpectroscopyActive: false,
			structureDisplay: 'normal',
			chartDisplay: 'normal',
			currentChartType: 'model'
		}
	},
	computed: {
		group1Title() {
			return (this.data && this.data.simulation_metal_1_name) ? this.data.simulation_metal_1_name : 'Group 1'
		},
		group2Title() {
			return (this.data && this.data.simulation_metal_2_name) ? this.data.simulation_metal_2_name : 'Group 2'
		},
		currentGroupTitle() {
			return this.currentGroup === 'group1' ? this.group1Title : this.group2Title
		},
		group1Visible() {
			return !!(this.data && (this.data.simulation_metal_1 || this.data.experiment_metal_1))
		},
		group2Visible() {
			return !!(this.data && (this.data.simulation_metal_2 || this.data.experiment_metal_2))
		},
		isCurrentGroupLoading() {
			const g = this.xanesLoading[this.currentGroup]
			return !!(g && (g.simulation || g.experiment))
		},
		isCurrentGroupDataEmpty() {
			const g = this.xanesData[this.currentGroup]
			return !g || ((g.simulation || []).length === 0 && (g.experiment || []).length === 0)
		},
		currentGroupEnergyRange() {
			const g = this.xanesData[this.currentGroup] || { simulation: [], experiment: [] }
			const energies = []
				.concat((g.simulation || []).map(item => item[0]))
				.concat((g.experiment || []).map(item => item[0]))
				.filter(val => !isNaN(val))

			if (energies.length === 0) {
				return { min: 0, max: 10, offset: 0.5 }
			}

			const min = Math.min(...energies)
			const max = Math.max(...energies)
			const offset = (max - min) * 0.05
			return {
				min: min - offset,
				max: max + offset,
				offset
			}
		}
	},
	mounted() {
		this.getDetail(this.$route.query.id)
	},
	beforeUnmount() {
		if (this.xanesChartInstance) {
			this.xanesChartInstance.dispose()
			this.xanesChartInstance = null
		}
		if (this.handleResize) {
			window.removeEventListener('resize', this.handleResize)
			this.handleResize = null
		}
		if (this.checkInterval) {
			clearInterval(this.checkInterval)
			this.checkInterval = null
		}
	},
	methods: {
		buildModelIframeSrc(cdrPath) {
			if (!cdrPath || typeof cdrPath !== 'string') return ''
			const apiBase = localStorage.getItem('api') || ''
			const ts = Date.now().toString(36) + Math.random().toString(36).slice(2)
			const qs = new URLSearchParams()
			qs.set('cdr_path', cdrPath)
			if (apiBase) qs.set('api', apiBase)
			qs.set('timestamp', ts)
			return `/model.html?${qs.toString()}`
		},

		getDetail(id) {
			this.$api.news.getDetail(id).then((res) => {
				this.data = res.data.data
				if (this.data) {
					const firstPath = (res.data.data.cdr_path && res.data.data.cdr_path[0]) ? res.data.data.cdr_path[0] : ''
					if (firstPath) localStorage.setItem('cdr_path', firstPath)
					localStorage.setItem('api', 'http://106.53.213.190:8081')
					this.iframeSrc = this.buildModelIframeSrc(firstPath)
					this.$nextTick(() => {
						this.loadXanesDataFromDetail()
					})
				}
			})
		},

		switchover(i) {
			this.currentChartType = 'model'
			const path = (this.data && this.data.cdr_path && this.data.cdr_path[i]) ? this.data.cdr_path[i] : ''
			if (path) localStorage.setItem('cdr_path', path)
			this.iframeSrc = this.buildModelIframeSrc(path)
		},

		toggleChartType() {
			this.currentChartType = 'line'
			this.$nextTick(() => {
				this.initXanesChart()
			})
		},

		switchGroup(groupKey) {
			this.currentGroup = groupKey
			this.$nextTick(() => {
				this.initXanesChart()
			})
		},

		normalizeCurveUrl(filePath) {
			let raw = filePath
			if (Array.isArray(raw)) raw = raw[0]
			if (!raw || typeof raw !== 'string') return ''
			const trimmed = raw.trim()
			if (!trimmed) return ''
			if (/^https?:\/\//i.test(trimmed)) return trimmed
			const apiBase = localStorage.getItem('api') || ''
			if (!apiBase) return trimmed
			if (trimmed.startsWith('/')) return apiBase.replace(/\/+$/, '') + trimmed
			return apiBase.replace(/\/+$/, '') + '/' + trimmed.replace(/^\/+/, '')
		},

		async loadXanesDataFromDetail() {
			if (!this.group1Visible && this.group2Visible) {
				this.currentGroup = 'group2'
			} else {
				this.currentGroup = 'group1'
			}

			const tasks = []
			if (this.data && this.data.simulation_metal_1) {
				tasks.push(this.loadSingleXanesData('group1', 'simulation', this.data.simulation_metal_1))
			}
			if (this.data && this.data.experiment_metal_1) {
				tasks.push(this.loadSingleXanesData('group1', 'experiment', this.data.experiment_metal_1))
			}
			if (this.data && this.data.simulation_metal_2) {
				tasks.push(this.loadSingleXanesData('group2', 'simulation', this.data.simulation_metal_2))
			}
			if (this.data && this.data.experiment_metal_2) {
				tasks.push(this.loadSingleXanesData('group2', 'experiment', this.data.experiment_metal_2))
			}

			Promise.all(tasks).finally(() => {
				this.$nextTick(() => {
					setTimeout(() => {
						this.initXanesChart()
					}, 100)
				})
			})
		},

		async loadSingleXanesData(groupKey, lineKey, filePath) {
			if (!this.xanesLoading[groupKey]) return
			this.xanesLoading[groupKey][lineKey] = true
			try {
				const url = this.normalizeCurveUrl(filePath)
				if (!url) throw new Error(`Empty ${groupKey}.${lineKey} file path`)
				const response = await fetch(url)
				if (!response.ok) throw new Error(`Failed to load ${groupKey}.${lineKey} data`)
				const text = await response.text()
				this.parseXanesData(text, groupKey, lineKey)
			} catch (error) {
				console.error(`Error loading ${groupKey}.${lineKey} XANES data:`, error)
				if (this.xanesData[groupKey]) this.xanesData[groupKey][lineKey] = []
			} finally {
				this.xanesLoading[groupKey][lineKey] = false
			}
		},

		parseXanesData(text, groupKey, lineKey) {
			const lines = text.trim().split('\n')
			const parsedData = []
			lines.forEach(line => {
				const trimmedLine = line.trim()
				if (!trimmedLine || trimmedLine.startsWith('#')) return
				const [energy, mu] = trimmedLine.split(/\s+/)
				if (energy && mu && !isNaN(energy) && !isNaN(mu)) {
					parsedData.push([parseFloat(energy), parseFloat(mu)])
				}
			})
			if (this.xanesData[groupKey]) this.xanesData[groupKey][lineKey] = parsedData
		},

		initXanesChart() {
			if (this.currentChartType !== 'line' || this.isCurrentGroupDataEmpty) return

			const initChart = () => {
				const dom = this.$refs.xanesChart
				if (!dom) return false

				const rect = dom.getBoundingClientRect()
				if (rect.width === 0 || rect.height === 0) return false

				if (this.xanesChartInstance) {
					this.xanesChartInstance.dispose()
					this.xanesChartInstance = null
				}

				this.xanesChartInstance = echarts.init(dom)

				const g = this.xanesData[this.currentGroup] || { simulation: [], experiment: [] }
				const series = []
				const legendData = []

				const makeSeries = (name, color, data, withArea) => ({
					name,
					type: 'line',
					data,
					smooth: true,
					lineStyle: { color, width: 2 },
					symbol: 'none',
					areaStyle: withArea
						? {
							color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
								{ offset: 0, color: `${color}33` },
								{ offset: 1, color: `${color}08` }
							])
						}
						: undefined,
					emphasis: {
						lineStyle: { width: 3 },
						focus: 'series'
					}
				})

				if ((g.simulation || []).length > 0) {
					series.push(makeSeries('Simulation', '#7db5f5', g.simulation, true))
					legendData.push('Simulation')
				}
				if ((g.experiment || []).length > 0) {
					series.push(makeSeries('Experiment', '#f59a7d', g.experiment, true))
					legendData.push('Experiment')
				}

				const { min, max } = this.currentGroupEnergyRange

				const option = {
					backgroundColor: 'transparent',
					tooltip: {
						trigger: 'axis',
						formatter: (params) => {
							let html = `<div>Energy: ${params[0].axisValue.toFixed(2)} eV</div>`
							params.forEach(param => {
								html += `<div>${param.seriesName}: μ = ${param.data[1].toFixed(6)} a.u.</div>`
							})
							return html
						},
						textStyle: { fontSize: 12 },
						backgroundColor: 'rgba(255,255,255,0.95)',
						borderColor: '#e0e0e0',
						borderWidth: 1,
						shadowBlur: 3,
						shadowColor: 'rgba(0,0,0,0.1)',
						padding: [8, 12]
					},
					legend: {
						top: '0%',
						right: '5%',
						textStyle: { fontSize: 11, color: '#666' },
						data: legendData
					},
					grid: {
						left: '12%',
						right: '5%',
						top: '15%',
						bottom: '12%',
						containLabel: true
					},
					xAxis: {
						type: 'value',
						name: 'Energy (eV)',
						nameLocation: 'middle',
						nameGap: 25,
						nameTextStyle: {
							color: '#666',
							fontSize: 12,
							fontWeight: 'bold'
						},
						axisLine: { lineStyle: { color: '#e0e0e0' } },
						axisTick: { lineStyle: { color: '#e0e0e0' } },
						axisLabel: {
							color: '#666',
							fontSize: 11,
							margin: 8
						},
						splitLine: { lineStyle: { color: '#f5f5f5' } },
						min,
						max
					},
					yAxis: {
						type: 'value',
						name: 'μ (a.u.)',
						nameGap: 15,
						nameRotate: 0,
						nameTextStyle: {
							color: '#666',
							fontSize: 12,
							fontWeight: 'bold',
							align: 'right',
							verticalAlign: 'middle',
							padding: [0, 10, 0, 0]
						},
						axisLine: { lineStyle: { color: '#e0e0e0' } },
						axisTick: { lineStyle: { color: '#e0e0e0' } },
						axisLabel: {
							color: '#666',
							fontSize: 11,
							margin: 8,
							formatter: function(val) {
								const absVal = Math.abs(val)
								if (absVal === 0) return '0'
								if (absVal < 0.001) return val.toExponential(1)
								if (absVal >= 1000) return val.toExponential(1)
								if (absVal >= 1) return val.toFixed(1)
								return val.toFixed(3)
							}
						},
						splitLine: { lineStyle: { color: '#f5f5f5' } }
					},
					series
				}

				this.xanesChartInstance.setOption(option)

				this.handleResize = () => {
					this.xanesChartInstance && this.xanesChartInstance.resize()
				}
				window.addEventListener('resize', this.handleResize)

				return true
			}

			if (!initChart()) {
				const checkContainer = () => {
					const dom = this.$refs.xanesChart
					if (dom) {
						const rect = dom.getBoundingClientRect()
						if (rect.width > 0 && rect.height > 0) {
							clearInterval(this.checkInterval)
							this.checkInterval = null
							initChart()
						}
					}
				}
				this.checkInterval = setInterval(checkContainer, 100)

				setTimeout(() => {
					if (this.checkInterval) {
						clearInterval(this.checkInterval)
						this.checkInterval = null
					}
				}, 10000)
			}
		},

		handleSpectroscopyClick() {
			this.isSpectroscopyActive = true
			if (this.chartDisplay === 'min') {
				this.chartDisplay = 'normal'
			}
			this.currentChartType = 'line'
			this.$nextTick(() => {
				this.initXanesChart()
			})
		},

		toggleLayout(target, state) {
			if (target === 'structure') {
				this.structureDisplay = state
				if (state === 'max') {
					this.chartDisplay = 'min'
				} else if (state === 'normal' && this.chartDisplay === 'min') {
					this.chartDisplay = 'normal'
				}
			} else if (target === 'chart') {
				this.chartDisplay = state
				if (state === 'max') {
					this.structureDisplay = 'min'
				} else if (state === 'normal' && this.structureDisplay === 'min') {
					this.structureDisplay = 'normal'
				}
			}
			if (state === 'max' || state === 'min') {
				this.isSpectroscopyActive = false
			}
			this.$nextTick(() => {
				this.initXanesChart()
			})
		}
	},
	watch: {
		currentGroup() {
			this.initXanesChart()
		}
	}
}
</script>

<style scoped>
* {
	padding: 0;
	margin: 0;
	box-sizing: border-box;
}

.bg {
	background-color: rgb(242, 242, 242);
}

.page {
	width: 80%;
	margin: 0 auto;
	min-height: 100vh;
	padding: 30px 0;
	font-family: 'Open Sans Variable', sans-serif;
}

.title {
	margin-bottom: 40px;
}

.title_h3 {
	font-size: 48px;
	font-weight: 600;
	color: rgba(33, 33, 47, 1);
	margin-bottom: 10px;
}

.title_sub {
	font-size: 24px;
	color: rgba(125, 181, 245, 1);
	margin-bottom: 15px;
}

.model_title {
	font-size: 20px;
	color: rgba(109, 115, 122, 1);
}

.box1 {
	display: flex;
	gap: 24px;
	margin-bottom: 30px;
	transition: all 0.3s ease;
}

.box1.spectroscopy-active {
	--structure-width: 20%;
}

.structure {
	background: white;
	border-radius: 20px;
	padding: 25px;
	transition: all 0.3s ease;
	overflow: hidden;
}

.layout-control {
	display: flex;
	gap: 8px;
	margin-bottom: 15px;
}

.layout-control button {
	padding: 4px 10px;
	border: 1px solid #2A6EBE;
	border-radius: 4px;
	background: #fff;
	color: #2A6EBE;
	cursor: pointer;
	font-size: 12px;
	transition: all 0.2s;
}

.layout-control .chart-type-btn {
	background: #2A6EBE;
	color: #fff;
}

.layout-control button:hover {
	background: #2A6EBE;
	color: #fff;
}

.structure_title {
	font-size: 22px;
	font-weight: 500;
	color: rgba(154, 156, 170, 1);
	margin-bottom: 20px;
	border-bottom: 1px solid #f0f0f0;
	padding-bottom: 10px;
}

.box {
	max-height: 300px;
	overflow-y: auto;
}

.box_list {
	min-height: 45px;
	background: rgba(248, 249, 250, 1);
	border-radius: 10px;
	margin-bottom: 12px;
	display: flex;
	align-items: center;
	line-height: 1.2;
	font-size: 15px;
	color: rgba(60, 93, 130, 1);
	padding: 0 15px;
	cursor: pointer;
	transition: all 0.3s;
}

.box_list:hover {
	background: rgba(235, 243, 255, 1);
}

.box_list span {
	margin-left: 8px;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

.chart_container {
	background: white;
	border-radius: 20px;
	padding: 25px;
	transition: all 0.3s ease;
}

.chart_title {
	font-size: 18px;
	color: rgba(60, 93, 130, 1);
	margin-bottom: 15px;
	font-weight: 500;
}

.chart_placeholder {
	width: 100%;
	height: 400px;
	border-radius: 10px;
	position: relative;
	overflow: hidden;
}

.chart-item {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	padding: 0 10px;
}

.property_section,
.oxygen_adsorbed_section {
	background: white;
	border-radius: 20px;
	padding: 25px;
	margin-bottom: 30px;
}

.section_title {
	font-size: 22px;
	font-weight: 500;
	color: #2A6EBE;
	margin-bottom: 20px;
	border-bottom: 1px solid #f0f0f0;
	padding-bottom: 10px;
}

.property_grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 15px;
}

.property_item {
	background: #f8f9fa;
	border-radius: 8px;
	padding: 12px 15px;
}

.item_label {
	font-size: 14px;
	color: #666;
	margin-right: 8px;
}

.item_value {
	font-size: 14px;
	color: #2A6EBE;
	font-weight: 500;
}

.model_switch_box {
	display: flex;
	gap: 10px;
}

.model_switch_btn {
	padding: 6px 16px;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	background: #f8f9fa;
	color: #2A6EBE;
	cursor: pointer;
	transition: all 0.2s;
	font-size: 14px;
}

.model_switch_btn.active {
	background: #7db5f5;
	color: white;
	border-color: #7db5f5;
}

.model_switch_btn:hover:not(.active) {
	border-color: #7db5f5;
	color: #2A6EBE;
}

.xanes_chart_container {
	border-radius: 8px;
	background: #f8f9fa;
	min-height: 280px;
	position: relative;
}

.xanes_empty {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	color: #999;
	font-size: 14px;
	text-align: center;
	width: 100%;
}

.el-descriptions {
	--el-descriptions-item-padding: 12px 8px;
}

.el-descriptions-item__label {
	font-size: 14px;
	color: #666;
	font-weight: 500;
}

.el-descriptions-item__content {
	font-size: 14px;
	color: #2A6EBE !important;
}

.bottom_section {
	display: flex;
	gap: 24px;
	margin-bottom: 30px;
}

.bottom_item {
	flex: 1;
	background: white;
	border-radius: 20px;
	padding: 25px;
}

.bottom_title {
	font-size: 22px;
	font-weight: 500;
	color: rgba(154, 156, 170, 1);
	margin-bottom: 15px;
	border-bottom: 1px solid #f0f0f0;
	padding-bottom: 10px;
}

.bottom_content {
	font-size: 16px;
	color: #333;
	line-height: 1.6;
}

.performance-item {
	margin-bottom: 10px;
}

.synthesis-method {
	font-weight: 500;
	padding: 30px;
	background: rgba(248, 249, 250, 1);
	border-radius: 8px;
}

::-webkit-scrollbar {
	display: none;
}

.box2 {
	display: grid;
	grid-template-columns: 4fr 6fr;
	gap: 30px;
}
</style>